import {
  type LabeledEdgeProps,
  normalizeTextLabelFontFamily,
  normalizeTextLabelFontSize,
} from "@/pages/canvas/types/canvasNodeData";
import { getFloatingEdgeParams } from "@/pages/canvas/utils/floatingEdge";
import { cn } from "@/utils";
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  getSmoothStepPath,
  getStraightPath,
  useInternalNode,
} from "@xyflow/react";
import { useEffect, useRef, useState } from "react";
import { useCanvasApi } from "../context/CanvasContext";
import { useCanvasUi } from "../context/CanvasUiContext";

export type {
  CanvasLabeledEdge,
  LabeledEdgeData,
} from "@/pages/canvas/types/canvasNodeData";

/**
 * Custom RF edge type: renders a bezier/step/straight path plus an optional
 * label at the path midpoint (React Flow's native `label` field). Click the
 * label text to edit it inline; double-click the edge (see CanvasPage's
 * onEdgeDoubleClick) adds a label to an unlabeled edge.
 * Registered as `edgeTypes={{ labeled: LabeledEdge }}` in CanvasPage.
 */
export function LabeledEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerStart,
  markerEnd,
  style,
  label,
}: LabeledEdgeProps) {
  const edgeData = data ?? {};
  const curveType = edgeData.curve_type ?? "step";

  // Figma-style floating endpoints: re-derive the attachment point/side from
  // the live node geometry so dragging never leaves an awkward opposing-handle
  // connection. Subscribes to the internal node store, so it recomputes on
  // every drag frame. Falls back to RF's handle-derived coords when a node
  // isn't measured yet (initial render).
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  const floating = getFloatingEdgeParams(sourceNode, targetNode);

  const sx = floating?.sx ?? sourceX;
  const sy = floating?.sy ?? sourceY;
  const tx = floating?.tx ?? targetX;
  const ty = floating?.ty ?? targetY;
  const sPos = floating?.sourcePos ?? sourcePosition;
  const tPos = floating?.targetPos ?? targetPosition;

  const straightArgs = { sourceX: sx, sourceY: sy, targetX: tx, targetY: ty };
  const curvedArgs = {
    sourceX: sx,
    sourceY: sy,
    sourcePosition: sPos,
    targetX: tx,
    targetY: ty,
    targetPosition: tPos,
  };

  const [edgePath, labelX, labelY] =
    curveType === "straight"
      ? getStraightPath(straightArgs)
      : curveType === "step"
        ? getSmoothStepPath(curvedArgs)
        : getBezierPath(curvedArgs);

  const { canEdit, updateEdgeLabel } = useCanvasApi();
  const { editingEdgeId, setEditingEdgeId } = useCanvasUi();
  const isEditing = editingEdgeId === id;

  const labelText = typeof label === "string" ? label : "";
  const [draft, setDraft] = useState(labelText);
  const inputRef = useRef<HTMLInputElement>(null);
  // Set by Escape so the draft is discarded. Tearing down the editor unmounts
  // the focused input, which can fire `onBlur` → `commit` and persist the draft
  // we meant to throw away; this flag lets the blur handler bail. Reset each
  // time we (re)enter edit mode.
  const cancelledRef = useRef(false);

  // Seed the draft and focus when entering edit mode.
  useEffect(() => {
    if (!isEditing) return;
    cancelledRef.current = false;
    setDraft(labelText);
    const raf = requestAnimationFrame(() => inputRef.current?.select());
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const commit = () => {
    // Escape requested a cancel — close without writing the draft.
    if (cancelledRef.current) {
      setEditingEdgeId(null);
      return;
    }
    updateEdgeLabel(id, draft);
    setEditingEdgeId(null);
  };

  // Figma-style label: plain text on the canvas-colored background (bg-n-05 —
  // same token as the React Flow pane in light and dark) that occludes the line
  // behind it, no pill chrome. Single fixed, theme-aware style for now; per-label
  // font/size/color comes later. Font matches the default text label.
  const labelClass =
    "bg-n-05 text-foreground leading-none rounded-[3px] px-1 py-0.5 whitespace-nowrap";
  // Mirrors the default text-label style (Inter, 20px, normal weight) so labels
  // read consistently across the canvas.
  const labelFontStyle = {
    fontFamily: normalizeTextLabelFontFamily(undefined),
    fontSize: normalizeTextLabelFontSize(undefined),
    fontWeight: "normal",
  } as const;
  const placeholderText = "Add text";

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerStart={markerStart}
        markerEnd={markerEnd}
        style={style}
      />
      {(labelText || isEditing) && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan absolute"
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: "all",
            }}
          >
            {isEditing ? (
              <input
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onPointerDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") commit();
                  if (e.key === "Escape") {
                    cancelledRef.current = true;
                    setEditingEdgeId(null);
                  }
                }}
                placeholder={placeholderText}
                style={{
                  ...labelFontStyle,
                  // Wide enough for the placeholder when empty (a 4ch min cut it
                  // off to "Add"); +1ch for caret/padding breathing room.
                  width: `${Math.max(draft.length, placeholderText.length) + 1}ch`,
                }}
                className={cn(
                  labelClass,
                  "text-center outline-none ring-1 ring-canvas-selection/70",
                )}
              />
            ) : (
              // Click the label text to edit it (no dedicated toolbar button).
              <div
                role={canEdit ? "button" : undefined}
                style={labelFontStyle}
                className={cn(labelClass, canEdit && "cursor-text")}
                onClick={(e) => {
                  if (!canEdit) return;
                  e.stopPropagation();
                  setEditingEdgeId(id);
                }}
              >
                {labelText}
              </div>
            )}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
